// src/screens/HomeScreen.js
// ============================================================================
// HOME — the lobby is now Home (evergreen Step 1.2, 2026-09-22)
//
// Founder direction 2026-09-20: "We place users on the main lobby, shift
// events to off tab." The old Lobby (the events list) lives on as the Events
// tab; this screen is the front door for every member and for guests.
//
// Never empty: with nobody live it still shows creator schedules, upcoming
// events, followed videos and the streamers rail. One composite callable
// (homeFeed) feeds it; the client only applies the same hide-flag and
// game-visibility filters the Lobby applies to events.
// ============================================================================

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  View, Text, ScrollView, TouchableOpacity, Image, StyleSheet,
  ActivityIndicator, RefreshControl, useWindowDimensions,
} from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { Ionicons } from '@expo/vector-icons';
import { db } from '../../firebaseConfig';
import Header from '../components/Header';
import Footer from '../components/Footer';
import NavBar from '../components/NavBar';
import { streamGameName } from '../constants/streamGames';
import { handOffStreamCard } from '../utils/watchCardHandoff';
import { useGameAvailability } from '../hooks/useGameAvailability';
import { videoTierChip } from '../utils/membershipTiers';

const COLORS = {
  BG: '#0b0b0b', CARD: '#111', CARD_BORDER: '#222', GOLD: '#FCD000', CYAN: '#00e5ff',
  TEXT: '#EDEDED', DIM: '#888', LIVE: '#e02222',
};
const POLL_MS = 60000;

const gameLabel = (s) => (s?.game === 'other' && s?.gameCustom ? s.gameCustom : (s?.game ? streamGameName(s.game) : null));
const fmtViewers = (n) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n || 0));
const fmtWhen = (ms) => {
  if (!ms) return '';
  const d = new Date(ms);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  if (sameDay) return `Today ${time}`;
  const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
  if (d.toDateString() === tomorrow.toDateString()) return `Tomorrow ${time}`;
  return `${d.toLocaleDateString(undefined, { weekday: 'short' })} ${time}`;
};
const fmtAgo = (ms) => {
  if (!ms) return null;
  const days = Math.floor((Date.now() - ms) / 86400000);
  if (days <= 0) return 'Live today';
  if (days === 1) return 'Live yesterday';
  if (days < 30) return `Live ${days}d ago`;
  return null;
};
const initials = (name) => (name || '??').replace(/[^a-zA-Z0-9 ]/g, '').split(' ')
  .map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || '??';

// ---------------------------------------------------------------------------
// Cards
// ---------------------------------------------------------------------------
const Avatar = ({ uri, name, size = 36 }) => (
  uri
    ? <Image source={{ uri }} style={{ width: size, height: size, borderRadius: size / 2, backgroundColor: '#222' }} />
    : <View style={{ width: size, height: size, borderRadius: size / 2, backgroundColor: '#1d1d1d', alignItems: 'center', justifyContent: 'center' }}>
        <Text style={{ color: COLORS.DIM, fontSize: size * 0.36, fontWeight: '800' }}>{initials(name)}</Text>
      </View>
);

const LiveCard = ({ s, width, onPress }) => (
  <TouchableOpacity style={[styles.liveCard, { width }]} onPress={onPress} activeOpacity={0.85}>
    <View style={styles.thumbWrap}>
      {s.thumbnailUrl ? <Image source={{ uri: s.thumbnailUrl }} style={styles.thumb} resizeMode="cover" /> : <View style={[styles.thumb, styles.thumbEmpty]} />}
      <View style={styles.liveBadge}><Text style={styles.liveBadgeTxt}>LIVE</Text></View>
      <View style={styles.viewersBadge}><Ionicons name="eye" size={11} color="#fff" /><Text style={styles.viewersTxt}>{fmtViewers(s.viewers)}</Text></View>
      {s.inMatch ? <View style={styles.matchBadge}><Text style={styles.matchBadgeTxt}>IN A MATCH</Text></View> : null}
    </View>
    <View style={styles.cardBody}>
      <Avatar uri={s.avatarUrl} name={s.displayName} />
      <View style={{ flex: 1, marginLeft: 8 }}>
        <Text style={styles.cardTitle} numberOfLines={1}>{s.title || `${s.displayName} is live`}</Text>
        <Text style={styles.cardSub} numberOfLines={1}>{s.displayName}{gameLabel(s) ? ` · ${gameLabel(s)}` : ''}{s.followed ? ' · Following' : ''}</Text>
      </View>
    </View>
  </TouchableOpacity>
);

const SquadCard = ({ t, width, onPress }) => (
  <TouchableOpacity style={[styles.liveCard, { width }]} onPress={onPress} activeOpacity={0.85}>
    <View style={[styles.thumbWrap, styles.squadThumb]}>
      <View style={styles.squadAvatars}>
        {(t.liveMembers || []).slice(0, 4).map((m) => <View key={m.uid} style={{ marginRight: -8 }}><Avatar uri={m.avatarUrl} name={m.userName} size={40} /></View>)}
      </View>
      <View style={styles.liveBadge}><Text style={styles.liveBadgeTxt}>SQUAD LIVE</Text></View>
      <View style={styles.viewersBadge}><Ionicons name="eye" size={11} color="#fff" /><Text style={styles.viewersTxt}>{fmtViewers(t.totalViewers)}</Text></View>
    </View>
    <View style={styles.cardBody}>
      <View style={{ flex: 1 }}>
        <Text style={styles.cardTitle} numberOfLines={1}>{t.name}</Text>
        <Text style={styles.cardSub} numberOfLines={1}>{t.liveCount} of {t.memberCount} live{(t.games || []).length ? ` · ${t.games.map((g) => streamGameName(g) || g).join(', ')}` : ''}</Text>
      </View>
    </View>
  </TouchableOpacity>
);

const VideoCard = ({ v, width, onPress }) => (
  <TouchableOpacity style={[styles.liveCard, { width }]} onPress={onPress} activeOpacity={0.85}>
    <View style={styles.thumbWrap}>
      {v.thumbnailUrl ? <Image source={{ uri: v.thumbnailUrl }} style={styles.thumb} resizeMode="cover" /> : <View style={[styles.thumb, styles.thumbEmpty]}><Ionicons name="play-circle" size={34} color="#333" /></View>}
      {v.durationSeconds ? <View style={styles.durBadge}><Text style={styles.durTxt}>{Math.floor(v.durationSeconds / 60)}m</Text></View> : null}
      {/* Members-only (3.5a): a lock on cards this viewer can't watch yet (the
          server drops any a Join would not unlock), a plain chip otherwise.
          The feed refetches on focus, so a join clears the lock. */}
      {v.membersOnly === true ? (
        <View style={[styles.membersBadge, v.locked === true && styles.membersBadgeLocked]}>
          <Ionicons name={v.locked === true ? 'lock-closed' : 'star'} size={10} color={COLORS.GOLD} />
          <Text style={styles.membersBadgeTxt}>{videoTierChip(v.membersMinTier) || 'Members'}</Text>
        </View>
      ) : null}
    </View>
    <View style={styles.cardBody}>
      <Avatar uri={v.owner?.avatarUrl} name={v.owner?.displayName} size={30} />
      <View style={{ flex: 1, marginLeft: 8 }}>
        <Text style={styles.cardTitle} numberOfLines={1}>{v.title || 'Video'}</Text>
        <Text style={styles.cardSub} numberOfLines={1}>{v.owner?.displayName}{v.views ? ` · ${fmtViewers(v.views)} views` : ''}</Text>
      </View>
    </View>
  </TouchableOpacity>
);

const SoonRow = ({ item, onPress }) => (
  <TouchableOpacity style={styles.soonRow} onPress={onPress} activeOpacity={0.8}>
    <View style={styles.soonWhen}><Text style={styles.soonWhenTxt}>{fmtWhen(item.atMs)}</Text></View>
    {item.type === 'stream'
      ? <>
          <Avatar uri={item.avatarUrl} name={item.displayName} size={32} />
          <View style={{ flex: 1, marginLeft: 10 }}>
            <Text style={styles.soonTitle} numberOfLines={1}>{item.displayName}</Text>
            <Text style={styles.soonSub} numberOfLines={1}>Scheduled stream{gameLabel(item) ? ` · ${gameLabel(item)}` : ''}{item.followed ? ' · Following' : ''}</Text>
          </View>
        </>
      : <>
          <View style={styles.soonEventIcon}><Ionicons name="trophy" size={16} color={COLORS.GOLD} /></View>
          <View style={{ flex: 1, marginLeft: 10 }}>
            <Text style={styles.soonTitle} numberOfLines={1}>{item.name}</Text>
            <Text style={styles.soonSub} numberOfLines={1}>Event{item.gameName || (item.game && streamGameName(item.game)) ? ` · ${item.gameName || streamGameName(item.game)}` : ''}{item.maxEntrants ? ` · ${item.entrantsCount}/${item.maxEntrants}` : ''}</Text>
          </View>
        </>}
    <Ionicons name="chevron-forward" size={16} color="#444" />
  </TouchableOpacity>
);

const StreamerChip = ({ s, onPress }) => (
  <TouchableOpacity style={styles.chip} onPress={onPress} activeOpacity={0.8}>
    <Avatar uri={s.avatarUrl} name={s.displayName} size={44} />
    <Text style={styles.chipName} numberOfLines={1}>{s.displayName}</Text>
    <Text style={styles.chipSub} numberOfLines={1}>{s.nextStreamAtMs ? fmtWhen(s.nextStreamAtMs) : (fmtAgo(s.lastLiveMs) || (s.followerCount ? `${fmtViewers(s.followerCount)} followers` : 'Streamer'))}</Text>
  </TouchableOpacity>
);

const EventChip = ({ e, onPress }) => (
  <TouchableOpacity style={styles.eventChip} onPress={onPress} activeOpacity={0.8}>
    <Text style={styles.eventWhen}>{fmtWhen(e.startAtMs)}</Text>
    <Text style={styles.eventName} numberOfLines={2}>{e.name}</Text>
    <Text style={styles.eventSub} numberOfLines={1}>{e.gameName || (e.game && streamGameName(e.game)) || 'Event'}{e.maxEntrants ? ` · ${e.entrantsCount}/${e.maxEntrants}` : ''}</Text>
  </TouchableOpacity>
);

const Section = ({ title, action, onAction, children }) => (
  <View style={styles.section}>
    <View style={styles.sectionHead}>
      <Text style={styles.sectionTitle}>{title}</Text>
      {action ? <TouchableOpacity onPress={onAction}><Text style={styles.sectionAction}>{action} →</Text></TouchableOpacity> : null}
    </View>
    {children}
  </View>
);

// ---------------------------------------------------------------------------
// Screen
// ---------------------------------------------------------------------------
export default function HomeScreen({ navigation }) {
  const isFocused = useIsFocused();
  const functions = getFunctions();
  const { width } = useWindowDimensions();
  const isLoggedIn = !!getAuth().currentUser;
  const { isEventVisible } = useGameAvailability();

  const [feed, setFeed] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [hidden, setHidden] = useState({ heats: false, tournaments: false });

  // Same per-mode soft-hide the Lobby honors (settings/events is world-readable).
  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'settings', 'events'), (snap) => {
      const d = snap.exists() ? snap.data() : {};
      setHidden({ heats: d.hideHeats === true, tournaments: d.hideTournaments === true });
    }, () => {});
    return unsub;
  }, []);

  const load = useCallback(async () => {
    try {
      const fn = httpsCallable(functions, 'homeFeed');
      const { data } = await fn({});
      setFeed(data || null);
    } catch (err) {
      console.warn('[Home] feed failed:', err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [functions]);

  useEffect(() => {
    if (!isFocused) return;
    load();
    const poll = setInterval(load, POLL_MS);
    return () => clearInterval(poll);
  }, [isFocused, load]);

  const onRefresh = () => { setRefreshing(true); load(); };

  const eventOk = useCallback((e) => {
    const m = String(e?.mode || '').toLowerCase();
    if ((m === 'racing' && hidden.heats) || (m === 'bracket' && hidden.tournaments)) return false;
    return isEventVisible({ game: e.game });
  }, [hidden, isEventVisible]);

  const live = feed?.live || [];
  const squads = feed?.squads || [];
  const soon = useMemo(() => (feed?.startingSoon || []).filter((x) => x.type !== 'event' || eventOk(x)), [feed, eventOk]);
  const videos = feed?.followedVideos || [];
  const rail = feed?.streamers || [];
  const events = useMemo(() => (feed?.events || []).filter(eventOk), [feed, eventOk]);

  const wide = width >= 900;
  const cardW = wide ? 300 : Math.min(300, width - 48);
  const nothingLive = !live.length && !squads.length;
  const nothingAtAll = feed && nothingLive && !soon.length && !videos.length && !rail.length && !events.length;

  const openStream = (s) => {
    if (s.inMatch?.eventId) navigation.navigate('EventWatch', { eventId: s.inMatch.eventId });
    else {
      // The card's picture goes over in memory, not in the URL (10-15).
      handOffStreamCard(s.userName || s.userId, s);
      navigation.navigate('StreamWatch', { channel: s.userName || s.userId });
    }
  };
  const openProfile = (s) => {
    if (!s.userName || s.userName === 'CritCash streamer') return navigation.navigate('StreamWatch', { channel: s.userId });
    return navigation.navigate('PublicProfile', { userName: s.userName });
  };
  const openSoon = (item) => (item.type === 'event'
    ? navigation.navigate('EventDetailScreen', { eventId: item.eventId })
    : openProfile(item));

  return (
    <View style={styles.container}>
      <Header navigation={navigation} />
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.content}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={COLORS.GOLD} />}
      >
        {loading && !feed ? (
          <View style={styles.loading}><ActivityIndicator color={COLORS.GOLD} size="large" /></View>
        ) : null}

        {feed && nothingLive ? (
          <View style={styles.quietBar}>
            <Ionicons name="moon" size={14} color={COLORS.DIM} />
            <Text style={styles.quietTxt}>Nobody is live right now.{soon.length ? ' Here is what is coming up.' : ''}</Text>
            <TouchableOpacity style={styles.quietBtn} onPress={() => navigation.navigate(isLoggedIn ? 'StreamingAccounts' : 'SignUp')}>
              <Text style={styles.quietBtnTxt}>{isLoggedIn ? 'Go live' : 'Join CritCash'}</Text>
            </TouchableOpacity>
          </View>
        ) : null}

        {live.length ? (
          <Section title="Live now" action="All streamers" onAction={() => navigation.navigate('Streams')}>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.hRow}>
              {live.map((s) => <LiveCard key={s.userId} s={s} width={cardW} onPress={() => openStream(s)} />)}
            </ScrollView>
          </Section>
        ) : null}

        {squads.length ? (
          <Section title="Squads live">
            <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.hRow}>
              {squads.map((t) => <SquadCard key={t.teamStreamId} t={t} width={cardW} onPress={() => navigation.navigate('TeamStream', { teamStreamId: t.teamStreamId })} />)}
            </ScrollView>
          </Section>
        ) : null}

        {soon.length ? (
          <Section title="Starting soon" action={events.length ? 'All events' : null} onAction={() => navigation.navigate('Lobby')}>
            <View style={styles.soonList}>
              {soon.map((item) => <SoonRow key={`${item.type}-${item.userId || item.eventId}-${item.atMs}`} item={item} onPress={() => openSoon(item)} />)}
            </View>
          </Section>
        ) : null}

        {videos.length ? (
          <Section title="From channels you follow">
            <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.hRow}>
              {videos.map((v) => <VideoCard key={v.id} v={v} width={cardW} onPress={() => navigation.navigate('VideoWatch', { videoId: v.id })} />)}
            </ScrollView>
          </Section>
        ) : null}

        {rail.length ? (
          <Section title="Streamers" action="Browse all" onAction={() => navigation.navigate('Streams')}>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.hRow}>
              {rail.map((s) => <StreamerChip key={s.userId} s={s} onPress={() => (s.live ? openStream(s) : openProfile(s))} />)}
            </ScrollView>
          </Section>
        ) : null}

        {events.length ? (
          <Section title="Events" action="Events tab" onAction={() => navigation.navigate('Lobby')}>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.hRow}>
              {events.map((e) => <EventChip key={e.eventId} e={e} onPress={() => navigation.navigate('EventDetailScreen', { eventId: e.eventId })} />)}
            </ScrollView>
          </Section>
        ) : null}

        {feed && isLoggedIn && !feed.followingCount ? (
          <View style={styles.tipBar}>
            <Ionicons name="heart-outline" size={14} color={COLORS.CYAN} />
            <Text style={styles.tipTxt}>Follow a streamer and their next stream and videos show up here.</Text>
          </View>
        ) : null}

        {nothingAtAll ? (
          <View style={styles.emptyWrap}>
            <Text style={styles.emptyTitle}>Quiet for now</Text>
            <Text style={styles.emptySub}>Be the first one live tonight, or set up your channel so followers know when to come back.</Text>
            <View style={styles.emptyBtns}>
              <TouchableOpacity style={styles.emptyBtn} onPress={() => navigation.navigate(isLoggedIn ? 'StreamingAccounts' : 'SignUp')}><Text style={styles.emptyBtnTxt}>{isLoggedIn ? 'Streaming hub' : 'Create an account'}</Text></TouchableOpacity>
              <TouchableOpacity style={[styles.emptyBtn, styles.emptyBtnGhost]} onPress={() => navigation.navigate('Lobby')}><Text style={[styles.emptyBtnTxt, styles.emptyBtnGhostTxt]}>Browse events</Text></TouchableOpacity>
            </View>
          </View>
        ) : null}

        <Footer navigation={navigation} />
      </ScrollView>
      <NavBar navigation={navigation} />
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: COLORS.BG },
  scroll: { flex: 1 },
  content: { paddingBottom: 24 },
  loading: { paddingVertical: 60, alignItems: 'center' },
  quietBar: { flexDirection: 'row', alignItems: 'center', gap: 8, marginHorizontal: 16, marginTop: 14, padding: 10, borderRadius: 10, backgroundColor: '#101010', borderWidth: 1, borderColor: COLORS.CARD_BORDER, flexWrap: 'wrap' },
  quietTxt: { color: COLORS.DIM, fontSize: 12, flex: 1, minWidth: 160 },
  quietBtn: { backgroundColor: COLORS.GOLD, borderRadius: 8, paddingVertical: 6, paddingHorizontal: 12 },
  quietBtnTxt: { color: '#000', fontSize: 12, fontWeight: '800' },
  section: { marginTop: 18 },
  sectionHead: { flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between', paddingHorizontal: 16, marginBottom: 8 },
  sectionTitle: { color: COLORS.TEXT, fontSize: 16, fontWeight: '900', letterSpacing: 0.2 },
  sectionAction: { color: COLORS.GOLD, fontSize: 12, fontWeight: '700' },
  hRow: { paddingHorizontal: 16, gap: 12 },
  liveCard: { backgroundColor: COLORS.CARD, borderRadius: 12, borderWidth: 1, borderColor: COLORS.CARD_BORDER, overflow: 'hidden' },
  thumbWrap: { width: '100%', aspectRatio: 16 / 9, backgroundColor: '#161616' },
  thumb: { width: '100%', height: '100%' },
  thumbEmpty: { alignItems: 'center', justifyContent: 'center', backgroundColor: '#161616' },
  squadThumb: { alignItems: 'center', justifyContent: 'center', backgroundColor: '#12161a' },
  squadAvatars: { flexDirection: 'row', paddingLeft: 8 },
  liveBadge: { position: 'absolute', top: 8, left: 8, backgroundColor: COLORS.LIVE, borderRadius: 4, paddingHorizontal: 6, paddingVertical: 2 },
  liveBadgeTxt: { color: '#fff', fontSize: 10, fontWeight: '900', letterSpacing: 0.5 },
  viewersBadge: { position: 'absolute', bottom: 8, left: 8, flexDirection: 'row', alignItems: 'center', gap: 4, backgroundColor: 'rgba(0,0,0,0.65)', borderRadius: 4, paddingHorizontal: 6, paddingVertical: 2 },
  viewersTxt: { color: '#fff', fontSize: 11, fontWeight: '700' },
  matchBadge: { position: 'absolute', top: 8, right: 8, backgroundColor: COLORS.GOLD, borderRadius: 4, paddingHorizontal: 6, paddingVertical: 2 },
  matchBadgeTxt: { color: '#000', fontSize: 10, fontWeight: '900' },
  durBadge: { position: 'absolute', bottom: 8, right: 8, backgroundColor: 'rgba(0,0,0,0.65)', borderRadius: 4, paddingHorizontal: 6, paddingVertical: 2 },
  durTxt: { color: '#fff', fontSize: 11, fontWeight: '700' },
  membersBadge: { position: 'absolute', top: 8, left: 8, flexDirection: 'row', alignItems: 'center', gap: 4, backgroundColor: 'rgba(0,0,0,0.75)', borderRadius: 4, paddingHorizontal: 6, paddingVertical: 2 },
  membersBadgeLocked: { borderWidth: 1, borderColor: 'rgba(252,208,0,0.55)' },
  membersBadgeTxt: { color: COLORS.GOLD, fontSize: 10, fontWeight: '800' },
  cardBody: { flexDirection: 'row', alignItems: 'center', padding: 10 },
  cardTitle: { color: COLORS.TEXT, fontSize: 13, fontWeight: '800' },
  cardSub: { color: COLORS.DIM, fontSize: 12, marginTop: 2 },
  soonList: { marginHorizontal: 16, backgroundColor: COLORS.CARD, borderRadius: 12, borderWidth: 1, borderColor: COLORS.CARD_BORDER, overflow: 'hidden' },
  soonRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 10, paddingHorizontal: 12, borderBottomWidth: 1, borderBottomColor: '#1a1a1a' },
  soonWhen: { width: 96, marginRight: 8 },
  soonWhenTxt: { color: COLORS.GOLD, fontSize: 12, fontWeight: '800' },
  soonTitle: { color: COLORS.TEXT, fontSize: 13, fontWeight: '700' },
  soonSub: { color: COLORS.DIM, fontSize: 11, marginTop: 1 },
  soonEventIcon: { width: 32, height: 32, borderRadius: 16, backgroundColor: '#1a1608', alignItems: 'center', justifyContent: 'center' },
  chip: { width: 96, alignItems: 'center', paddingVertical: 10, paddingHorizontal: 6, backgroundColor: COLORS.CARD, borderRadius: 12, borderWidth: 1, borderColor: COLORS.CARD_BORDER },
  chipName: { color: COLORS.TEXT, fontSize: 12, fontWeight: '800', marginTop: 6, maxWidth: 84 },
  chipSub: { color: COLORS.DIM, fontSize: 10, marginTop: 2, maxWidth: 84 },
  eventChip: { width: 200, backgroundColor: COLORS.CARD, borderRadius: 12, borderWidth: 1, borderColor: COLORS.CARD_BORDER, padding: 12 },
  eventWhen: { color: COLORS.GOLD, fontSize: 11, fontWeight: '800' },
  eventName: { color: COLORS.TEXT, fontSize: 13, fontWeight: '800', marginTop: 4 },
  eventSub: { color: COLORS.DIM, fontSize: 11, marginTop: 4 },
  tipBar: { flexDirection: 'row', alignItems: 'center', gap: 8, marginHorizontal: 16, marginTop: 18, padding: 10, borderRadius: 10, backgroundColor: '#0d1416', borderWidth: 1, borderColor: '#123' },
  tipTxt: { color: COLORS.DIM, fontSize: 12, flex: 1 },
  emptyWrap: { marginHorizontal: 16, marginTop: 24, padding: 20, borderRadius: 12, backgroundColor: COLORS.CARD, borderWidth: 1, borderColor: COLORS.CARD_BORDER, alignItems: 'center' },
  emptyTitle: { color: COLORS.TEXT, fontSize: 18, fontWeight: '900' },
  emptySub: { color: COLORS.DIM, fontSize: 13, marginTop: 6, textAlign: 'center', maxWidth: 420 },
  emptyBtns: { flexDirection: 'row', gap: 10, marginTop: 14, flexWrap: 'wrap', justifyContent: 'center' },
  emptyBtn: { backgroundColor: COLORS.GOLD, borderRadius: 8, paddingVertical: 8, paddingHorizontal: 14 },
  emptyBtnTxt: { color: '#000', fontSize: 13, fontWeight: '800' },
  emptyBtnGhost: { backgroundColor: 'transparent', borderWidth: 1, borderColor: '#333' },
  emptyBtnGhostTxt: { color: COLORS.TEXT },
});
